import { Country, DE_LOCALE_COUNTRIES, REACT_APP_API_URL } from 'helpers/conf';
import { DEFAULT_LOCALE, languages } from 'languages';
import { isPrerender } from 'modules/common/helpers/prerender';
import {
  addLocaleToPath,
  getLocaleBasename,
  getLocaleFromPath,
  stripLocaleFromPath,
} from './paths';
import { getStoredLocale, setStoredLocale } from './storage';

// First render waits for this — never block on a slow API.
const IP_LOOKUP_TIMEOUT_MS = 1500;

let bootLocale: string = DEFAULT_LOCALE;

export const getBootBasename = () => getLocaleBasename(bootLocale);

const replacePathname = (pathname: string) => {
  const { search, hash } = window.location;
  window.history.replaceState(
    window.history.state,
    '',
    `${pathname}${search}${hash}`
  );
};

// `undefined` = lookup failed/timed out.
const detectLocaleByIp = async (): Promise<string | undefined> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), IP_LOOKUP_TIMEOUT_MS);

  try {
    const response = await fetch(`${REACT_APP_API_URL}/api/current_ip`, {
      signal: controller.signal,
    });
    const { country_code: rawCountryCode } = await response.json();
    const countryCode = DE_LOCALE_COUNTRIES.includes(rawCountryCode)
      ? Country.Germany
      : rawCountryCode;

    const countryLanguages = languages[countryCode];
    const language =
      countryLanguages?.find(({ locale }) => locale === navigator.language) ||
      countryLanguages?.[0] ||
      languages[Country.UnitedKingdom][0];

    return language.locale;
  } catch {
    return;
  } finally {
    clearTimeout(timeoutId);
  }
};

// `undefined` = no reliable signal: use the default without persisting it.
const resolveUnprefixedLocale = async (): Promise<string | undefined> => {
  // Bots always get exactly what the URL says.
  if (isPrerender()) return;

  return getStoredLocale() ?? (await detectLocaleByIp());
};

// Before the router exists: canonicalize the URL prefix, or add the stored/IP
// locale prefix in place (no reload).
export const resolveBootLocale = async (): Promise<string> => {
  const { pathname } = window.location;
  const urlLocale = getLocaleFromPath(pathname);

  if (urlLocale) {
    const canonicalPathname = addLocaleToPath(
      stripLocaleFromPath(pathname),
      urlLocale
    );
    if (canonicalPathname !== pathname) replacePathname(canonicalPathname);

    bootLocale = urlLocale;
    if (!isPrerender()) setStoredLocale(urlLocale);
  } else {
    const detectedLocale = await resolveUnprefixedLocale();
    if (!detectedLocale) return bootLocale;

    if (detectedLocale !== DEFAULT_LOCALE) {
      replacePathname(addLocaleToPath(pathname, detectedLocale));
    }

    bootLocale = detectedLocale;
    setStoredLocale(detectedLocale);
  }

  return bootLocale;
};

let bootLocalePromise: Promise<string> | undefined;

// Started once; only i18n init and the router wait for it.
export const getBootLocalePromise = () => {
  bootLocalePromise =
    bootLocalePromise ?? resolveBootLocale().catch(() => bootLocale);
  return bootLocalePromise;
};
